import { FlexibleContentCard } from '../flexible-content-card/FlexibleContentCard';
import type { CardSpanColumns } from '../flexible-content-card/types';
import type {
  FlexibleContentGridColumns,
  FlexibleContentGridResolved,
} from './types';

// Static map — Tailwind cannot resolve a runtime value in a class name.
// `grid-cols-1` below `md` gives the spec's "on mobile each content card is
// full page width"; the default `align-items: stretch` plus the card's
// `h-full` gives the shared row height with top-aligned content.
const GRID_COLS: Record<FlexibleContentGridColumns, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 md:grid-cols-2',
  '3': 'grid-cols-1 md:grid-cols-3',
  '4': 'grid-cols-1 md:grid-cols-4',
};

// A card's own `spanColumns`. Only the `md:` variants exist: below `md` the
// grid is a single column, so every card is full width there anyway and a span
// has nothing to widen. `md:col-span-1` is the no-op default and still emitted
// explicitly, so the class is never absent and Tailwind keeps all four in the
// build (a runtime-built `md:col-span-${n}` would be purged).
const COL_SPAN: Record<CardSpanColumns, string> = {
  '1': 'md:col-span-1',
  '2': 'md:col-span-2',
  '3': 'md:col-span-3',
  '4': 'md:col-span-4',
};

/**
 * The card's wish, capped at the grid's width. An editor setting a span of 4
 * on a two-column grid means "as wide as it goes", not an overflowing card —
 * Tailwind's `col-span-4` in a 2-column grid would add two implicit columns
 * and break every other row of the grid, so the cap is load-bearing, not
 * cosmetic.
 */
const spanClass = (
  span: CardSpanColumns,
  columns: FlexibleContentGridColumns,
): string =>
  COL_SPAN[
    (Math.min(Number(span), Number(columns)) || 1).toString() as CardSpanColumns
  ];

export const FlexibleContentGridComponent = ({
  resolved,
}: {
  resolved: FlexibleContentGridResolved;
}) => {
  const cards = resolved.cards ?? [];
  if (cards.length === 0) {
    return null;
  }

  // The phone pair the backend resolved is only handed to the cards when the
  // editor asked for it. The chain (widget → location → domain) practically
  // always yields a number, so without this gate every card lacking its own
  // `ctaLink`/`ctaPhone` would inherit a phone CTA it was never meant to show
  // (SH-428). A card's own CTA fields are untouched by the toggle.
  const fallbackPhone = resolved.showInheritedPhoneCta
    ? resolved.phone
    : undefined;
  const fallbackMatelsoPhoneLabel = resolved.showInheritedPhoneCta
    ? resolved.matelsoPhoneLabel
    : undefined;

  return (
    <section className="pt-4">
      {/* Nested blocks get no width signal from SlotRenderer, so the widget
          brings its own page container. */}
      <div>
        <div className={`grid gap-6 ${GRID_COLS[resolved.columns]}`}>
          {cards.map((card, i) => (
            <FlexibleContentCard
              key={card.id || i}
              card={card}
              background={resolved.background}
              fallbackPhone={fallbackPhone}
              fallbackMatelsoPhoneLabel={fallbackMatelsoPhoneLabel}
              iconSize={resolved.iconSize}
              padding={resolved.cardPadding}
              className={spanClass(card.spanColumns, resolved.columns)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
